<template>
  <!-- Vai para o `body`: a tela do app é um contexto de empilhamento próprio, e dentro
       dele a barra de abas pintava por cima da folha — o rodapé dela ficava cortado.
       Fora, ela cobre a tela inteira, que é o que uma folha modal precisa fazer. -->
  <Teleport to="body">
    <div class="scrim" @click.self="$emit('close')">
      <div class="sheet" role="dialog" aria-modal="true" :aria-label="title">
        <ConfettiBurst v-if="didWin" />
        <div class="grab" />

        <!-- Recolher no topo. A folha é longa — placar, XP, quebra por palavra, próximos
             modos e o texto de compartilhar —, e fechá-la exigia rolar até o fim. -->
        <button class="collapse" type="button" aria-label="Recolher o resultado" @click="$emit('close')">
          <AppIcon name="x" size="lg" />
        </button>

        <!-- O número é o prêmio: ele ocupa o topo inteiro, no gradiente da marca, e o resto
             da folha explica de onde ele veio. -->
        <div class="result">
          <span class="lbl axis" :class="{ lost: !didWin }">{{ title }}</span>
          <b class="score">{{ counter }}</b>
          <p class="under">{{ subtitle }}</p>
          <span v-if="isRecord" class="record"><AppIcon name="crown" size="sm" />recorde</span>
        </div>

        <div v-if="scored" class="xp">
          <div class="xp-top">
            <AppIcon name="trending" size="sm" />
            <span class="lbl lbl-ink">Nível {{ level }} · {{ levelTitle }}</span>
            <span class="lbl">{{ xpIntoLevel }} / {{ xpPerLevel }} XP</span>
          </div>
          <div class="xp-track"><div class="xp-fill" :style="{ width: xpPct }" /></div>
        </div>

        <div class="breakdown">
          <div
            v-for="(word, i) in words"
            :key="word.position"
            class="bd-row"
            :class="rowClass(word)"
            :style="{ animationDelay: `${120 + i * 70}ms` }"
          >
            <AppIcon :name="rowIcon(word)" size="sm" />
            <span class="bd-word">{{ word.word.toUpperCase() }}</span>
            <span class="bd-meta">{{ rowMeta(word) }}</span>
            <span class="bd-pts">{{ points(word) }}</span>
          </div>
        </div>

        <!-- Duelo: o bônus de tempo e o caminho de volta para o placar comparado. -->
        <div v-if="duelOutcome" class="duel">
          <div class="duel-account">
            <span><b class="num">{{ duelOutcome.score }}</b><small>pontos</small></span>
            <AppIcon name="plus" size="sm" />
            <span><b class="num amber">{{ duelOutcome.bonus }}</b><small>bônus de tempo</small></span>
            <span class="equal">=</span>
            <span><b class="num total">{{ duelOutcome.total }}</b><small>total</small></span>
          </div>
          <p class="duel-note">
            {{ duelOutcome.closed
              ? 'Todos jogaram: o resultado está no duelo.'
              : 'Agora é esperar quem falta jogar — o placar de cada um só aparece no fim.' }}
          </p>
          <button class="btn btn-cta" type="button" @click="$emit('duel', duelOutcome.duel_id)">
            <AppIcon name="zap" size="sm" />{{ duelOutcome.closed ? 'Ver o resultado' : 'Ver o duelo' }}
          </button>
        </div>

        <!-- Fase da trilha: estrelas e XP no lugar da lista de modos. Quem veio da trilha
             quer voltar para ela, não trocar de modo. -->
        <div v-if="trailOutcome" class="phase">
          <div class="phase-stars">
            <AppIcon
              v-for="e in 3"
              :key="e"
              name="star"
              size="lg"
              :filled="e <= trailOutcome.stars"
              :class="{ dimmed: e > trailOutcome.stars }"
            />
          </div>
          <p class="phase-note">
            <template v-if="trailOutcome.back_to_chapter_start">
              Sem energia: a trilha voltou para o início do capítulo.
            </template>
            <template v-else-if="trailOutcome.xp_awarded">
              Fase {{ trailOutcome.phase }} concluída · +{{ trailOutcome.xp_awarded }} XP
            </template>
            <template v-else-if="didWin">
              Fase {{ trailOutcome.phase }} de novo — o XP de uma fase é pago uma vez.
            </template>
            <template v-else>
              Restam {{ trailOutcome.lives }} vida(s).
            </template>
          </p>
        <button class="btn btn-cta" type="button" @click="$emit('trail')">
          <AppIcon name="flag" size="sm" />Voltar à trilha
        </button>
      </div>

      <!-- Quem termina a diária não descobria que existe outro modo: a folha fechava e a
           tela voltava para a mesma chain encerrada. -->
      <!-- Resultado aberto por cima de uma partida em andamento: a única saída oferecida é
           voltar para ela. Listar modos aqui convidaria a abandonar o que está aberto. -->
      <div v-if="gameOpen" class="back-to-game">
        <button class="btn btn-cta" type="button" @click="$emit('close')">
          <AppIcon name="gamepad-2" size="sm" />Voltar para a partida em andamento
        </button>
      </div>

      <div v-else-if="!trailOutcome && !duelOutcome" class="upcoming">
        <span class="lbl upcoming-title">e agora</span>

        <button
          v-for="mode in otherModes"
          :key="mode.key"
          class="next"
          type="button"
          @click="$emit('play', mode.key)"
        >
          <span class="next-brand" :class="mode.is_daily ? 'of-daily' : 'of-training'">
            <AppIcon :name="mode.is_daily ? 'calendar-days' : 'refresh-cw'" size="lg" />
          </span>
          <span class="next-text">
            <b>{{ mode.name }}</b>
            <span>{{ mode.is_daily ? 'a mesma para todo mundo, e pontua' : 'quantas partidas quiser, sem pontuar' }}</span>
          </span>
          <AppIcon name="chevron-right" size="lg" class="next-arrow" />
        </button>

        <!-- A trilha some da lista de modos porque não é modo de partida — e sumia daqui
             também, que é onde o jogador acabou de ficar sem o que jogar. -->
        <button class="next" type="button" @click="$emit('trail')">
          <span class="next-brand of-trail"><AppIcon name="flag" size="lg" /></span>
          <span class="next-text">
            <b>Trilha</b>
            <span>fases em ordem, com estrelas e XP</span>
          </span>
          <AppIcon name="chevron-right" size="lg" class="next-arrow" />
        </button>

        <button class="next" type="button" @click="$emit('duels')">
          <span class="next-brand of-duel"><AppIcon name="swords" size="lg" /></span>
          <span class="next-text">
            <b>Duelo</b>
            <span>a mesma chain contra quem você chamar</span>
          </span>
          <AppIcon name="chevron-right" size="lg" class="next-arrow" />
        </button>
      </div>

      <!-- Quando vira a próxima diária. O instante vem do servidor e é o mesmo para todo
           mundo; aqui só descontamos o relógio local a partir dele. -->
      <p v-if="showCount" class="flip">
        <AppIcon name="clock" size="sm" />
        <span>Próxima chain do dia em <b class="num">{{ count }}</b></span>
      </p>

      <!-- Compartilhar sai do cliente e não leva palavra nenhuma: o texto é a forma do
           result, não a chain. -->
      <div class="share">
        <pre class="preview" aria-label="Prévia do texto">{{ shareText }}</pre>
        <button class="btn btn-outline" type="button" @click="compartilharResultado">
          <AppIcon name="link" size="sm" />{{ shareLabel }}
        </button>
      </div>

      <div v-if="!gameOpen" class="actions">
        <!-- Com a lista de modos acima, um botão de treino aqui seria o mesmo caminho duas
             vezes na mesma folha. Ele só existe quando não há lista. -->
        <button
          v-if="repeat && !otherModes.length"
          class="btn btn-cta"
          type="button"
          @click="$emit('again')"
        >
          <AppIcon name="refresh" size="sm" />{{ scored ? 'Jogar treino' : 'Nova chain' }}
        </button>
        <button class="btn btn-outline" type="button" @click="$emit('close')">
          <AppIcon name="eye" size="sm" />Ver tabuleiro
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import { reducedMotion } from '../../lib/motion';
import AppIcon from '../ui/AppIcon.vue';
import ConfettiBurst from './ConfettiBurst.vue';
import { share, buildText } from '../../services/share';
import { XP_POR_NIVEL } from '../../store';

const RETORNO = {
  shared: 'Compartilhado',
  copied: 'Copiado. Cole onde quiser',
  canceled: 'Compartilhar result',
  failed: 'Não deu para copiar aqui',
};

export default {
  name: 'EndSheet',
  components: { AppIcon, ConfettiBurst },
  props: {
    summary: { type: Object, required: true },
    scored: { type: Boolean, default: true },
    isRecord: { type: Boolean, default: false },
    level: { type: Number, default: 1 },
    levelTitle: { type: String, default: '' },
    xpIntoLevel: { type: Number, default: 0 },
    modes: { type: Array, default: () => [] },
    currentMode: { type: String, default: '' },
    trailOutcome: { type: Object, default: null },
    duelOutcome: { type: Object, default: null },
    /**
     * Há partida em andamento por baixo desta folha. Duelo não entra: dele não se volta —
     * sair da tela já encerrou a vez.
     */
    gameOpen: { type: Boolean, default: false },
  },
  emits: ['again', 'close', 'play', 'trail', 'duel', 'duels'],
  data() {
    return {
      counter: 0,
      xpPerLevel: XP_POR_NIVEL,
      remaining: 0,
      clock: null,
      guessFeedback: null,
    };
  },
  computed: {
    /** De onde veio a partida, para a etiqueta não chamar tudo de treino. */
    modeLabel() {
      if (this.duelOutcome) return 'duel';
      if (this.trailOutcome) return 'trail';
      return 'treino';
    },
    /** O nome do modo jogado, para o cabeçalho do texto. */
    modeName() {
      if (this.duelOutcome) return 'Duelo';
      if (this.trailOutcome) return `Trilha · fase ${this.trailOutcome.phase}`;
      return this.modes.find((m) => m.key === this.currentMode)?.name || 'Sentido';
    },
    shareText() {
      return buildText({
        summary: this.summary,
        modeName: this.modeName,
        url: window.location.origin,
      });
    },
    shareLabel() {
      return RETORNO[this.guessFeedback] || 'Compartilhar result';
    },
    /** A contagem só faz sentido enquanto a diária de hoje já foi. */
    showCount() {
      return this.scored && this.remaining > 0;
    },
    count() {
      const hours = Math.floor(this.remaining / 3600);
      const minutos = Math.floor((this.remaining % 3600) / 60);
      const seconds = this.remaining % 60;
      return [hours, minutos, seconds].map((n) => String(n).padStart(2, '0')).join(':');
    },
    /** Os outros modos disponíveis — sem o que acabou de ser jogado. */
    otherModes() {
      return this.modes.filter((m) => m.key !== this.currentMode);
    },
    /** Só faz sentido oferecer "de novo" quando existe um modo de treino para repetir. */
    repeat() {
      return !this.trailOutcome && !this.duelOutcome && this.modes.some((m) => !m.is_daily);
    },
    didWin() {
      return this.summary.state === 'won';
    },
    title() {
      return this.didWin ? 'chain completa' : 'a chain parou aqui';
    },
    /** A linha sob o número: de onde ele veio, e onde a chain quebrou quando quebrou. */
    subtitle() {
      const parts = [`pontos · ${this.modeName.toLowerCase()}`];
      if (!this.scored) parts.push('não pontua');
      if (!this.didWin && this.missedWord) parts.push(`a palavra era ${this.missedWord}`);
      return parts.join(' · ');
    },
    words() {
      return this.summary.words;
    },
    missedWord() {
      return (this.words.find((w) => w.solved === false)?.word || '').toUpperCase();
    },
    xpPct() {
      return `${Math.round((this.xpIntoLevel / XP_POR_NIVEL) * 100)}%`;
    },
  },
  mounted() {
    this.scroll();
    this.remaining = this.summary.seconds_to_next_daily || 0;
    this.clock = setInterval(() => {
      this.remaining = Math.max(0, this.remaining - 1);
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.clock);
  },
  methods: {
    async compartilharResultado() {
      this.guessFeedback = await share(this.shareText);
      // O rótulo volta ao normal depois do aviso: botão que fica dizendo "copiado" some
      // como botão.
      setTimeout(() => { this.guessFeedback = null; }, 2600);
    },
    /** Rola o placar em vez de estampá-lo: o número ganhando peso é o prêmio. */
    scroll() {
      const target = this.summary.score;
      if (reducedMotion()) {
        this.counter = target;
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - start) / 900);
        this.counter = Math.round(target * (1 - Math.pow(1 - k, 3)));
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    rowClass(word) {
      return { ok: word.solved === true, node: word.solved === false, nu: word.solved === null };
    },
    rowIcon(word) {
      return { true: 'check', false: 'x', null: 'lock' }[String(word.solved)];
    },
    points(word) {
      if (word.solved === null) return '—';
      return word.solved ? `+${word.points}` : '0';
    },
    rowMeta(word) {
      if (word.solved === null) return 'não alcançada';
      if (word.solved === false) return 'não veio';
      return `${word.revealed_at_solve} de ${word.word.length} reveladas`;
    },
  },
};
</script>

<style scoped>
.duel {
  display: grid;
  gap: 10px;
  padding: 13px;
  border-radius: var(--r-button);
  border: 1px solid var(--line);
  background: var(--surface-2);
}

.duel-account {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.duel-account span { display: grid; justify-items: center; }
.duel-account small { color: var(--text-3); font-family: var(--f-label); font-size: 8.5px; text-transform: uppercase; }
.duel-account b { font-size: 20px; }
.duel-account .equal { color: var(--text-3); }
.duel-account .amber { color: var(--amber); }
.duel-account .total { color: var(--ok); }

.duel-note { margin: 0; text-align: center; color: var(--text-2); font-size: 12.5px; }

.share {
  display: grid;
  gap: 9px;
  padding: 12px;
  border-radius: var(--r-button);
  border: 1px solid var(--line);
  background: var(--surface-2);
}

.preview {
  margin: 0;
  /* Pilha com as fontes de emoji do sistema: sem elas o navegador desenha a caixa vazia,
     e a prévia pareceria defeito nosso. O texto copiado é sempre o mesmo. */
  font-family: var(--f-label), "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
  font-size: 11px;
  line-height: 1.5;
  color: var(--text-2);
  white-space: pre-wrap;
  word-break: break-word;
}

.flip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin: 0;
  padding: 9px 12px;
  border-radius: var(--r-button);
  border: 1px dashed var(--line-2);
  color: var(--text-2);
  font-size: 13px;
}

.flip b { color: var(--text); font-variant-numeric: tabular-nums; }

.phase {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: var(--r-button);
  border: 1px solid var(--line);
  background: var(--surface-2);
}

.phase-stars { display: flex; gap: 8px; color: var(--amber); }
.phase-stars .dimmed { color: var(--line-2); }
.phase-note { margin: 0; color: var(--text-2); font-size: 13px; text-align: center; }
.phase .btn { width: 100%; }

.scrim {
  position: fixed;
  inset: 0;
  background: var(--modal-veil);
  display: flex;
  align-items: flex-end;
  z-index: 20;
  animation: fade 0.22s ease-out;
  backdrop-filter: blur(3px);
}

@keyframes fade { from { opacity: 0; } to { opacity: 1; } }

.sheet {
  width: 100%;
  background: var(--surface);
  border-radius: var(--r-card-lg) var(--r-card-lg) 0 0;
  padding: 20px 18px calc(18px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 15px;
  animation: slide-up 0.38s cubic-bezier(0.22, 1, 0.36, 1);
  max-height: 94%;
  overflow-y: auto;
  position: relative;
}

@keyframes slide-up { from { transform: translateY(100%); } to { transform: none; } }

/* O X é o único que escapa do empilhamento: ele flutua no canto da folha. */
.sheet > *:not(.confetti):not(.collapse) { position: relative; z-index: 3; }

.collapse {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 4;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--surface-2);
  color: var(--text-3);
  cursor: pointer;
  transition: color 0.16s, border-color 0.16s;
}

.collapse:hover { color: var(--text); border-color: var(--line-2); }

.grab {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: var(--line-2);
  margin: -6px auto 0;
}

.result { display: grid; justify-items: center; gap: 6px; text-align: center; padding: 6px 0 2px; }

.axis { color: var(--blue-text); letter-spacing: 0.18em; }
.axis.lost { color: var(--error-light); }

/* Texto no gradiente da marca: é o único lugar da tela onde ela pinta letra. */
.score {
  font: 700 72px/1 var(--f-title);
  background: linear-gradient(135deg, var(--link-blue), var(--link-pink));
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.result .under { margin: 0; font: 600 13px/1.4 var(--f-body); color: var(--text-3); }

.record {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--f-label);
  font-size: 11px;
  padding: 4px 10px;
  border-radius: var(--r-pill);
  font-weight: 700;
  letter-spacing: 0.06em;
}

.record {
  background: var(--ok);
  color: var(--bg);
  animation: record-pop 0.5s 0.3s cubic-bezier(0.3, 1.6, 0.5, 1) both;
}

@keyframes record-pop {
  0% { transform: scale(0) rotate(-12deg); }
  100% { transform: none; }
}

.xp { display: flex; flex-direction: column; gap: 6px; }
.xp-top { display: flex; align-items: center; gap: 7px; color: var(--text-2); }
.xp-top .lbl:last-child { margin-left: auto; }
.lbl-ink { color: var(--text-2); }

.xp-track { height: 7px; border-radius: 4px; background: var(--raised); overflow: hidden; }

.xp-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--active), var(--magenta));
  box-shadow: var(--cta-shadow);
  transition: width 1s cubic-bezier(0.22, 1, 0.36, 1);
}

.breakdown { display: flex; flex-direction: column; gap: 8px; }

.bd-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 15px;
  border-radius: var(--r-button);
  border: 1px solid var(--line);
  background: var(--veil-1);
  font-size: 13px;
  animation: bd-in 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.bd-row.ok { background: var(--ok-veil); border-color: var(--ok-line); }
.bd-row.node { background: var(--error-veil); border-color: var(--error-line); }

@keyframes bd-in {
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: none; }
}

.bd-word {
  font: 700 16px/1 var(--f-title);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.bd-meta {
  margin-left: auto;
  font-family: var(--f-label);
  font-size: 9.5px;
  color: var(--text-3);
}

.bd-pts {
  font: 700 13px/1 var(--f-title);
  font-variant-numeric: tabular-nums;
  min-width: 44px;
  text-align: right;
  color: var(--amber);
}

.bd-row.ok .bd-pts, .bd-row.ok :deep(.ic) { color: var(--ok); }
.bd-row.node .bd-pts, .bd-row.node :deep(.ic) { color: var(--error); }
.bd-row.plain .bd-pts, .bd-row.plain :deep(.ic), .bd-row.plain .bd-word { color: var(--text-3); }

@media (min-width: 1024px) {
  .scrim { align-items: center; justify-content: center; }
  .sheet {
    max-width: 440px;
    border-radius: var(--r-card-lg);
    max-height: 88%;
    animation: zoom-in 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .grab { display: none; }
}

@keyframes zoom-in {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: none; opacity: 1; }
}

/* ── o que jogar depois ──────────────────────────────────────────────────────
   Linha inteira, como na folha de modos: em chip o nome ficava sozinho, e "Full-5"
   não diz a quem chegou agora o que vai acontecer se ele tocar ali. */
.upcoming { display: grid; gap: 8px; margin-top: 6px; }

.upcoming-title { color: var(--text-3); }

.next {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: var(--touch);
  padding: 11px 13px;
  border-radius: var(--r-card);
  border: 1px solid var(--line);
  background: var(--veil-1);
  color: var(--text);
  text-align: left;
  transition: border-color 0.16s, background 0.16s;
}

.next:hover { border-color: var(--active-line); background: var(--veil-2); }

.next-brand {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 13px;
  color: var(--bg);
}

.of-daily { background: linear-gradient(135deg, var(--link-blue), var(--violet)); }
.of-training { background: linear-gradient(135deg, var(--violet), var(--purple)); }
.of-trail { background: linear-gradient(135deg, var(--purple), var(--magenta)); }
.of-duel { background: linear-gradient(135deg, var(--magenta), var(--violet)); }

.next-text { min-width: 0; }
.next-text b { display: block; font: 700 14.5px/1.2 var(--f-body); }

.next-text span {
  display: block;
  margin-top: 3px;
  font: 600 12px/1.35 var(--f-body);
  color: var(--text-3);
}

.next-arrow { color: var(--text-3); }
/* A última fila ocupa a folha inteira: botão de meia largura no rodapé parecia recorte
   de outra coisa, e é ele que devolve o jogador ao tabuleiro. */
.actions { display: flex; flex-wrap: wrap; gap: 10px; }
.actions .btn { flex: 1 1 140px; }
.back-to-game .btn { width: 100%; }
</style>